import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, ArrowLeft, Smartphone, AlertCircle, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import PadaidLogo from '../../components/ui/PadaidLogo';
import { useAuth } from '../../contexts/AuthContext';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';
import { trackEvent } from '../../services/track';

const Login = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    totpCode: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(
        formData.email,
        formData.password,
        requires2FA ? formData.totpCode : null,
        rememberMe
      );

      if (result.requires2FA) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      if (result.success) {
        try { trackEvent({ type: 'login', url: window.location.href }); } catch(e){ console.debug('trackEvent login non bloquant', e); }
        const to = (result.user_type === 'fondateur')
          ? '/dashboard/fondateur'
          : (result.user_type === 'co-fondateur')
          ? '/dashboard/co-fondateur'
          : (result.user_type === 'membre')
          ? '/dashboard/membre'
          : '/dashboard/invite';
        navigate(to, { replace: true });
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const isFounderRestricted = error && error.toLowerCase().includes('fondateur restreint');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/auth" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'authentification
          </Link>
          <div className="flex justify-center mb-6">
            <PadaidLogo size="lg" showText={true} />
          </div>
          <h2 className="text-3xl font-bold text-foreground">Connexion</h2>
          <p className="mt-2 text-muted-foreground">Connectez-vous à votre espace PADAID</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-center flex items-center justify-center">
              <LogIn className="mr-2 h-5 w-5" />
              Se connecter
            </CardTitle>
            <CardDescription className="text-center">
              Accédez à votre espace personnel selon votre statut
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="mb-4" variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {isFounderRestricted ? (
                    <span className="inline-flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Accès fondateur restreint. Veuillez utiliser l'adresse email autorisée ou contacter l'administrateur.</span>
                  ) : (
                    error
                  )}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {!requires2FA ? (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Adresse email</Label>
                    <Input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleInputChange} placeholder="votre@email.com" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="password">Mot de passe</Label>
                    <div className="relative mt-1">
                      <Input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={formData.password} onChange={handleInputChange} placeholder="••••••••" className="pr-10" />
                      <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? (<EyeOff className="h-4 w-4 text-muted-foreground" />) : (<Eye className="h-4 w-4 text-muted-foreground" />)}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <Smartphone className="h-12 w-12 text-primary mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Entrez le code à 6 chiffres de votre application d'authentification (Google Authenticator, Authy, etc.)</p>
                  </div>
                  <div>
                    <Label htmlFor="totpCode">Code d'authentification</Label>
                    <Input id="totpCode" name="totpCode" type="text" value={formData.totpCode} onChange={(e) => { const value = e.target.value.replace(/\D/g, '').slice(0, 6); setFormData(prev => ({ ...prev, totpCode: value })); if (error) setError(''); }} placeholder="123456" required className="text-center text-lg tracking-widest mt-1" maxLength={6} autoComplete="one-time-code" />
                  </div>
                  <Button type="button" variant="ghost" onClick={() => { setRequires2FA(false); setFormData(prev => ({ ...prev, totpCode: '' })); }} className="w-full">← Retour à la connexion</Button>
                </>
              )}
              {!requires2FA && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="remember-me-label">
                    <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="h-4 w-4 rounded border-border accent-primary" data-testid="remember-me-checkbox" />
                    Me souvenir 30 jours
                  </label>
                  <div className="text-sm">
                    <Link to="/forgot-password" className="text-primary hover:text-primary/80 font-medium">Mot de passe oublié ?</Link>
                  </div>
                </div>
              )}
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>{loading ? (<div className="flex items-center"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>Connexion...</div>) : requires2FA ? ('Vérifier le code') : ('Se connecter')}</Button>
            </form>
            {!requires2FA && (
              <div className="mt-6">
                <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-card text-muted-foreground">Ou connectez-vous avec</span></div></div>
                <div className="mt-6"><SocialLoginButtons /></div>
                <div className="relative mt-6"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-card text-muted-foreground">Nouveau sur PADAID ?</span></div></div>
                <div className="mt-6"><Button variant="outline" className="w-full border-secondary text-secondary hover:bg-secondary hover:text-black" asChild><Link to="/register">Créer un compte</Link></Button></div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;